/**
 * App-level cross-feature event bus.
 *
 * Singleton instance for events that cross feature boundaries. Each event
 * variant is typed with its own payload. Subscribing to a specific `type`
 * narrows the payload automatically.
 *
 * Add new variants here as Phase 7 commit 68 migrates more
 * `window.dispatchEvent(new CustomEvent(...))` call sites away from the
 * untyped window-event channel. See FRONTEND_AGENT_RULES §4.
 */

import { createEventBus } from "./createEventBus";

export type CompanionPromptDisplay = {
  kind: "command";
  label: string;
  description?: string;
  source?: string;
};

export type CompanionDockAutoOpenReason =
  | "agent-dispatch"
  | "agent-activation"
  | "chat-injection"
  | "new-session"
  | "page-connect"
  | "route-entry"
  | "onboarding-showcase"
  | "overlay-host";

export type MarcusCursorSource = "onboarding" | "guided-flow";

export type AppEvent =
  /**
   * JWT auth state changed in the current tab. `storage` events do not fire
   * for the same document that writes tokens, so login/logout flows publish
   * here to keep AuthProvider in sync without a full browser reload.
   */
  | {
      type: "auth:state-changed";
      user: { id: string; email: string; name: string; role: string } | null;
    }
  /**
   * Asks the active companion panel to send `text` through the current chat.
   * Emitters include ads/product setup surfaces. Listener:
   * FloatingAgentCompanion or the active studio companion panel.
   *
   * Optional `images` mirror the `ChatAttachedImage` shape used by the
   * chat input — `url` may be hosted or a local data URL for preview,
   * while `dataUrl` is the vision payload sent to the runtime.
   *
   * Optional `display` lets callers send a rich internal prompt while the
   * chat renders a concise user-facing command card.
   */
  | {
      type: "companion:request-prompt";
      text: string;
      display?: CompanionPromptDisplay;
      images?: Array<{
        url: string;
        dataUrl?: string;
        filename: string;
        mediaType: string;
        type: "image" | "video";
      }>;
    }
  /**
   * Cross-feature request to expand the docked companion. The companion
   * store owns the allow/deny policy; emitters only describe the reason.
   */
  | {
      type: "companion:dock-auto-open";
      reason: CompanionDockAutoOpenReason;
    }
  /**
   * Fired on the first user turn of a Marcus session so its generic
   * placeholder name can be replaced with a topic-relevant title. The
   * companion publishes either a `presetTitle` (slash command) or the
   * `firstMessage` text (free-form chat); the session-owning hook
   * (`useMarcusGrowthSession`) derives + persists the title via
   * `setRuntimeSessionTitle` and updates the chat-history list.
   */
  | {
      type: "companion:session-title-suggested";
      sessionId: string;
      presetTitle?: string;
      firstMessage?: string;
    }
  /**
   * Fired by companion chat streams and DispatchedAgentCard (polling
   * fallback) when a sub-agent writes an artifact. Active studio surfaces
   * can re-pull loaders so their UI converges without a manual refresh.
   *
   * `artifactId` is set when the chat stream surfaced a tool result with
   * the new id; `viaPolling` flags the polling-fallback path.
   */
  | {
      type: "wizard:artifact-saved";
      toolName: string;
      artifactId?: string;
      childSessionId?: string;
      viaPolling?: boolean;
    }
  /**
   * Fired by companion panels when a `dispatch_*` tool call enters
   * `input-available`, so active wizard-like surfaces can show the current
   * section immediately.
   */
  | { type: "wizard:section-active"; toolName: string }
  /**
   * Companion → AdsWorkspace UI command. Emitter: features/ads
   * adsDispatcher (responds to `ads_*` tool calls). Listeners:
   * AdsWorkspace and ManualProductForm. The `command` discriminator is
   * a free-form string the dispatcher and listener agree on.
   */
  | {
      type: "ads-ui-action";
      command: string;
      params: Record<string, unknown>;
    }
  /**
   * Marcus autopilot overlay events. These replace older window
   * CustomEvent channels for the plan timeline and animated cursor.
   */
  | {
      type: "marcus-plan-announced";
      steps: Array<{ label: string; target?: string }>;
    }
  | {
      type: "marcus-plan-advance";
      target?: string;
      index?: number;
      labelHint?: string;
    }
  | {
      type: "marcus-cursor-move-to";
      stepId?: string;
      target: string;
      comment?: string;
      pulses?: number;
      durationMs?: number;
      landingPosition?: "top" | "center" | "bottom";
      showTooltip?: boolean;
      source?: MarcusCursorSource;
    }
  | {
      type: "marcus-cursor-arrived";
      stepId?: string;
      target: string;
      comment?: string;
      source?: MarcusCursorSource;
    }
  | {
      type: "marcus-cursor-step-complete";
      stepId: string;
      target: string;
      status?: "completed" | "timeout" | "cancelled" | "skipped";
    }
  /**
   * Fired by the Marcus companion after guided-flow cursor narration has
   * finished, timed out, or been intentionally skipped. Guided controllers
   * use this to advance visible handoffs only after Marcus has finished the
   * spoken explanation for the current highlighted UI.
   */
  | {
      type: "marcus-guided-flow:speech-complete";
      stepId?: string;
      target: string;
      comment?: string;
      status: "completed" | "timeout" | "cancelled" | "error" | "skipped";
      reason?: string;
    }
  | {
      type: "marcus-onboarding:agent-list-showcase";
      active: boolean;
    }
  /**
   * Final beat of the agent_list onboarding stage. The arrival handler emits
   * this when the cursor reaches the team-sweep target, prompting the chat
   * list view to "play" a piano-key style activation across each agent row.
   * `runId` lets row animations restart from scratch on replay.
   */
  | {
      type: "marcus-onboarding:agent-list-sweep";
      runId: number;
    }
  | {
      type: "marcus-onboarding:voice-preface-requested";
      runId: number;
      text: string;
    }
  | { type: "marcus-onboarding:compact-avatar-mounted" }
  | { type: "marcus-onboarding:compact-avatar-ready" }
  | { type: "marcus-onboarding:compact-avatar-unavailable" }
  /**
   * Manual debug/replay trigger for the Explore Marcus onboarding. The
   * controller opens the current intro dialog before sending the latest
   * onboarding start prompt.
   */
  | { type: "marcus-onboarding:restart" }
  /**
   * Skip-the-dialog onboarding trigger fired by the slash-command palette
   * (`/onboarding`). The controller calls `startOnboarding` immediately so
   * the user does not have to confirm a second time after selecting the
   * command. `label` flows into the persisted prompt label; `speakIntro`
   * defaults to true so the preface voice coordination matches the popup
   * accept path.
   */
  | {
      type: "marcus-onboarding:start-direct";
      label?: string;
      speakIntro?: boolean;
    }
  /**
   * Final-stage hand-off from Marcus onboarding to the static-adclone page.
   * The first signal fires after product photos are staged in the manual
   * form, so the overlay can move to the AI Add Product button. The second
   * is retained for compatibility with older callers that programmatically
   * kicked off image-ad discovery.
   */
  | { type: "marcus-onboarding:product-image-staged" }
  | { type: "marcus-onboarding:auto-discover-image-ads" }
  /**
   * Guided slash-flow product selection. The static/video adclone
   * controllers use this to distinguish a real user choice from the page's
   * default selected product.
   */
  | {
      type: "marcus-guided-flow:adclone-product-selected";
      flow: "static_adclone" | "video_adclone";
      productId: string | null;
    }
  /**
   * Clone-video render pipeline → adclone page handoff. Emitted only for the
   * final clone output: single 15s video for solo clones, stitched 30s MP4 for
   * paired clones. The adclone flow asks Core to import/reuse the editor
   * project, apply clone visual overlays, run STT, then opens `/videos/:id/edit`.
   */
  | {
      type: "ads:clone-final-video-ready";
      workspaceId: string;
      productId: string;
      productName?: string;
      videoUrl: string;
      thumbnailUrl?: string;
      jobId: string;
      finalResultId: string;
      videoIndex: number;
      planRunId?: string;
      cloneSourceArtifactId: string;
      sourceVideoScriptAngle?: string;
      paired: boolean;
      segmentAJobId?: string;
      segmentBJobId?: string;
      segmentBVideoIndex?: number;
      segmentBCloneSourceArtifactId?: string;
      segmentBOffsetSec?: number;
      durationSec?: number;
      artifactId?: string;
    }
  /**
   * Companion → ContentStudio UI command. Emitters:
   * contentStudioDispatcher, NovaCompanionPanel
   * (action="content_studio_ui" + tool-specific paths). Listeners:
   * ContentStudioWorkspace and the article page. Same shape as
   * ads-ui-action — `command` discriminates per UI action.
   */
  | {
      type: "content-studio-ui-action";
      command: string;
      params: Record<string, unknown>;
    }
  /**
   * Department-side dispatcher events. Emitted by legacy geo / growth /
   * social / brand dispatchers + the dispatchCompanionToolCall router
   * for non-companion-feature tool calls. Remaining listeners kick off
   * the relevant store action.
   *
   * Payloads stay loosely typed (`unknown` / `Record<string, unknown>`)
   * — listeners narrow internally with their own type guards.
   */
  | { type: "agent-run-node"; nodeId: string; autoProceed: boolean }
  | { type: "agent-start-research"; brandInput: unknown }
  | { type: "agent-start-social-research"; brandInput: unknown; brandId?: string }
  | {
      type: "agent-start-brand-research";
      brandInput: unknown;
      mode?: "create" | "refresh";
      targetBrandId?: string;
    }
  | { type: "agent-start-growth-research"; growthInput: unknown }
  | { type: "agent-start-geo-research"; geoInput: unknown }
  | { type: "agent-save-brand"; setActive: unknown }
  | { type: "agent-send-to-production"; detail: Record<string, unknown> }
  | { type: "agent-generate-template-preview"; detail: Record<string, unknown> }
  | { type: "agent-generate-social-video"; detail: Record<string, unknown> }
  /**
   * Optimistic product-created notification. Emitter:
   * ManualProductForm (after successful save). Listener:
   * useProductsData (adds to optimistic id ref so the new product
   * survives artifact refetches until the backend confirms it).
   */
  | { type: "product-created"; product: Record<string, unknown> }
  /**
   * Kling effects picker → companion card workflow. Emitter:
   * KlingEffectsPickerCard. (No live listener at the moment — kept
   * as a typed channel for re-introduction.)
   */
  | {
      type: "kling-effect-picked";
      effect_scene: string;
      name: string;
      effectType: "single" | "dual";
      category: string;
    }
  // Video-editor SSE pipeline events. Published by services/sse{Translate,
  // Export,Transcribe}.ts; the video-editor store's sseSlice subscribes and
  // updates per-project progress fields. `projectId` is always set so a
  // listener can scope state to one project.
  | {
      type: "video-editor:translate-progress";
      projectId: string;
      targetLanguage: string;
      stage: string;
      pct?: number;
      message?: string;
    }
  | {
      type: "video-editor:translate-result";
      projectId: string;
      targetLanguage: string;
      project: unknown;
    }
  | {
      type: "video-editor:translate-error";
      projectId: string;
      targetLanguage: string;
      message: string;
    }
  | {
      type: "video-editor:export-progress";
      projectId: string;
      format: "srt" | "vtt" | "mp4";
      stage: string;
      pct?: number;
      message?: string;
    }
  | {
      type: "video-editor:export-result";
      projectId: string;
      format: "srt" | "vtt" | "mp4";
      payload: unknown;
    }
  | {
      type: "video-editor:export-error";
      projectId: string;
      format: "srt" | "vtt" | "mp4";
      message: string;
    }
  | {
      type: "video-editor:transcribe-progress";
      projectId: string;
      stage: string;
      pct?: number;
      message?: string;
    }
  | {
      type: "video-editor:transcribe-result";
      projectId: string;
      project: unknown;
    }
  | {
      type: "video-editor:transcribe-error";
      projectId: string;
      message: string;
    }
  // Marcus refreshes the editor's project after a tool call mutates server
  // state. Published by features/companion or core, consumed by the
  // video-editor store to call setProject(p, { preserveDrafts: true }).
  | {
      type: "video-editor:refresh-project";
      projectId: string;
    }
  /**
   * Marcus -> Video editor live UI command. Server-persisted edits still go
   * through apply_video_edit_spec; this event lets tools focus panes,
   * control playback, and trigger saves in the mounted editor.
   */
  | {
      type: "video-editor-ui-action";
      command: "focus" | "playback" | "save";
      projectId: string;
      params: Record<string, unknown>;
    };

export const appEvents = createEventBus<AppEvent>();
